import React, { useState, useMemo, useEffect } from "react";
import { useScrollLock } from "../../../hooks/useScrollLock";
import TShirtSizeGuide from "../../../components/utils/TShirtSizeGuide";
import {
  FiShare2,
  FiMinus,
  FiPlus,
  FiCheck,
  FiTruck,
  FiShield,
  FiRefreshCw,
  FiZoomIn,
  FiArrowLeft,
} from "react-icons/fi";
import { LuRuler } from "react-icons/lu";

import {
  ActionsSection,
  AddToCartBtn,
  BandTag,
  Container,
  CurrentPrice,
  DiscountBadge,
  Feature,
  FeaturesList,
  GallerySection,
  MainImage,
  OptionLabel,
  OptionsSection,
  OriginalPrice,
  PriceRow,
  PriceSection,
  ProductInfo,
  ProductLayout,
  ProductTitle,
  QuantityBtn,
  QuantityControl,
  QuantityInput,
  SectionTitle,
  ShareBtn,
  SimilarProductsSection,
  SizeButton,
  SizeGrid,
  TabButton,
  TabContent,
  TabNavigation,
  TabSection,
  Thumbnail,
  ThumbnailGrid,
  ImageModal,
  ModalContent,
  CloseButton,
  ProductShowcase,
  LoadingContainer,
  ErrorContainer,
  NotFoundContainer,
  RequiredIndicator,
  StockIndicator,
  PriceNote,
  TabHeading,
  TabSubHeading,
  TabList,
  TabListItem,
  SpecTable,
  SpecRow,
  SpecCell,
  EmptyProductsContainer,
  BackLink,
  SizeGuideButton,
} from "./ProductPage.styles";

import MainContainer from "../../../components/utils/MainContainer";
import Particles from "../../../components/utils/Particles";
import ProductCard from "../../../components/utils/ProductCard";

import { formatPrice } from "../../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProduct,
  getSimilarProducts,
  getProductDimensions,
} from "../../../store/slices/products/asyncThunks";
import {
  selectSelectedProduct,
  selectSimilarProducts,
  selectProductDimensions,
} from "../../../store/slices/products/slice";
import { addToCart } from "../../../store/slices/cart/slice";
import { toast } from "../../../components/modern-ui/sonner";
import { trackBandView } from "../../../store/slices/viewTracking/asyncThunks";

export default function ProductPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data: currentProduct,
    loading,
    error,
  } = useSelector(selectSelectedProduct);

  const similarProducts = useSelector(selectSimilarProducts);
  const productDimensions = useSelector(selectProductDimensions);

  // State for main product
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [showImageModal, setShowImageModal] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  useScrollLock(showImageModal || showSizeGuide);

  useEffect(() => {
    dispatch(getProduct(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (currentProduct?.band?.id) {
      dispatch(trackBandView(currentProduct.band.id));
    }
  }, [currentProduct?.band?.id, dispatch]);

  useEffect(() => {
    if (currentProduct?.id) {
      dispatch(getSimilarProducts(currentProduct.id));
      dispatch(getProductDimensions(currentProduct.id));
    }
  }, [currentProduct?.id, dispatch]);


  // Funkcija za formatiranje dimenzija za size guide
  const formatDimensionsForSizeGuide = (dimensions) => {
    if (!dimensions) return null;

    const sizes = ['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];
    const formattedData = [];

    sizes.forEach(size => {
      const chest = dimensions[`chest${size}`];
      const length = dimensions[`length${size}`];
      const sleeve = dimensions[`sleeve${size}`];

      // Dodaj samo ako postoje dimenzije za tu veličinu
      if (chest || length || sleeve) {
        formattedData.push({
          size,
          chest: chest ? chest.toString() : '-',
          length: length ? length.toString() : '-',
          sleeve: sleeve ? sleeve.toString() : '-',
        });
      }
    });

    return formattedData.length > 0 ? formattedData : null;
  };

  // : Koristi prave slike iz API-ja
  const productImages = useMemo(() => {
    if (!currentProduct?.productImages) return [];

    // Sortuj slike po sortOrder ili koristi redosled iz API-ja
    const sortedImages = [...currentProduct.productImages].sort(
      (a, b) => a.sortOrder - b.sortOrder
    );
    return sortedImages.map((img) => img.url);
  }, [currentProduct?.productImages]);

  // : Reset selectedImage kada se promeni proizvod
  useEffect(() => {
    setSelectedImage(0);
    setSelectedSize(null);
    setQuantity(1);
  }, [currentProduct?.id]);

  // Izračunaj pravi procenat popusta
  const discountPercentage = useMemo(() => {
    if (!currentProduct?.price || !currentProduct?.salePrice) return 0;
    return Math.round(
      ((currentProduct.price - currentProduct.salePrice) /
        currentProduct.price) *
        100
    );
  }, [currentProduct?.price, currentProduct?.salePrice]);

  // : Generiši dostupne veličine na osnovu product fieldsova
  const availableSizes = useMemo(() => {
    if (!currentProduct) return [];

    const sizes = [];
    if (currentProduct.sizeS) sizes.push("S");
    if (currentProduct.sizeM) sizes.push("M");
    if (currentProduct.sizeL) sizes.push("L");
    if (currentProduct.sizeXL) sizes.push("XL");
    if (currentProduct.sizeXXL) sizes.push("XXL");
    if (currentProduct.sizeSM) sizes.push("SM");
    if (currentProduct.sizeLXL) sizes.push("LXL");

    return sizes;
  }, [currentProduct]);

  // Utility functions
  const handleQuantityChange = (action) => {
    if (action === "increment" && quantity < currentProduct.stockQuantity) {
      setQuantity((prev) => prev + 1);
    } else if (action === "decrement" && quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const handleAddToCart = () => {
    // Validacija veličine - potrebno je odabrati veličinu ako kategorija ima veličine
    if (currentProduct.category?.hasSizes && availableSizes.length > 0) {
      if (!selectedSize) {
        toast.error("Molimo odaberite veličinu pre dodavanja u korpu.");

        return;
      }
    }

    // Validacija stanja
    if (currentProduct.stockQuantity <= 0) {
      toast.error("Proizvod trenutno nije dostupan.");

      return;
    }

    // Validacija količine
    if (quantity > currentProduct.stockQuantity) {
      toast.error(
        `Maksimalna dostupna količina je ${currentProduct.stockQuantity} kom.`
      );

      return;
    }

    // Dodaj u korpu
    try {
      dispatch(
        addToCart({
          product: currentProduct,
          quantity,
          selectedSize: selectedSize,
        })
      );

      // Uspešna poruka

      toast.success(`${currentProduct.name} je uspešno dodat u korpu!`);

      // Reset stanja
      setQuantity(1);
      if (availableSizes.length > 0) {
        setSelectedSize(null);
      }
    } catch (error) {
      toast.error("Došlo je do greške prilikom dodavanja u korpu.");
    }
  };

  const shareProduct = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: currentProduct?.name,
          text: currentProduct?.description,
          url: window.location.href,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          navigator.clipboard.writeText(window.location.href);

          toast.success("Link je kopiran u clipboard!");
        }
      }
    } else {
      navigator.clipboard.writeText(window.location.href);

      toast.success("Link je kopiran u clipboard!");
    }
  };

  const openImageModal = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const handleBack = () => {
    navigate(-1);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  // Dodaj loading i error handling
  if (loading) {
    return (
      <MainContainer>
        <Particles />
        <Container>
          <BackLink onClick={handleBack}>
            <FiArrowLeft />
            Nazad
          </BackLink>
          <LoadingContainer>Učitavanje proizvoda...</LoadingContainer>
        </Container>
      </MainContainer>
    );
  }

  if (error) {
    return (
      <MainContainer>
        <Particles />
        <Container>
          <BackLink onClick={handleBack}>
            <FiArrowLeft />
            Nazad
          </BackLink>
          <ErrorContainer>
            Greška prilikom učitavanja proizvoda: {error}
          </ErrorContainer>
        </Container>
      </MainContainer>
    );
  }

  if (!currentProduct) {
    return (
      <MainContainer>
        <Particles />
        <Container>
          <BackLink onClick={handleBack}>
            <FiArrowLeft />
            Nazad
          </BackLink>
          <NotFoundContainer>Proizvod nije pronađen.</NotFoundContainer>
        </Container>
      </MainContainer>
    );
  }

  return (
    <>
      <MainContainer>
        <Particles />
        <Container>
          <BackLink onClick={handleBack}>
            <FiArrowLeft />
            Nazad
          </BackLink>
          <ProductLayout>
            <GallerySection>
              <MainImage onClick={openImageModal}>
                <img
                  src={productImages[selectedImage]}
                  alt={currentProduct.name}
                />
                <div className="zoom-overlay">
                  <FiZoomIn size={24} />
                </div>
              </MainImage>
              <ThumbnailGrid>
                {productImages.map((img, index) => (
                  <Thumbnail
                    key={index}
                    $active={selectedImage === index}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img
                      src={img}
                      alt={`${currentProduct.name} ${index + 1}`}
                    />
                  </Thumbnail>
                ))}
              </ThumbnailGrid>
            </GallerySection>

            <ProductInfo>
              <BandTag>{currentProduct.band?.name}</BandTag>
              <ProductTitle>{currentProduct.name}</ProductTitle>

              <PriceSection>
                <PriceRow>
                  <CurrentPrice>
                    {formatPrice(
                      currentProduct.salePrice || currentProduct.price
                    )}
                  </CurrentPrice>
                  {currentProduct.salePrice && currentProduct.price && (
                    <>
                      <OriginalPrice>
                        {formatPrice(currentProduct.price)}
                      </OriginalPrice>
                      <DiscountBadge>-{discountPercentage}%</DiscountBadge>
                    </>
                  )}
                </PriceRow>
                <PriceNote>Cena uključuje PDV</PriceNote>
              </PriceSection>

              {/* : Prikaži veličine ako kategorija ima veličine */}
              {currentProduct.category?.hasSizes &&
                availableSizes.length > 0 && (
                  <OptionsSection>
                    <OptionLabel>
                      Veličina:
                      {!selectedSize && (
                        <RequiredIndicator>(obavezno)</RequiredIndicator>
                      )}
                    </OptionLabel>
                    <SizeGrid>
                      {availableSizes.map((size) => (
                        <SizeButton
                          key={size}
                          $selected={selectedSize === size}
                          onClick={() => setSelectedSize(size)}
                        >
                          {size}
                        </SizeButton>
                      ))}
                    </SizeGrid>
                    {/* Prikaži dugme za vodič za veličine samo ako postoje dimenzije */}
                    {productDimensions.data && (
                      <SizeGuideButton onClick={() => setShowSizeGuide(true)}>
                        <LuRuler />
                        Vodič za veličine
                      </SizeGuideButton>
                    )}
                  </OptionsSection>
                )}

              <OptionsSection>
                <OptionLabel>
                  Količina:
                  <StockIndicator>
                    (dostupno: {currentProduct.stockQuantity} kom)
                  </StockIndicator>
                </OptionLabel>
                <QuantityControl>
                  <QuantityBtn
                    onClick={() => handleQuantityChange("decrement")}
                    disabled={quantity <= 1}
                  >
                    <FiMinus />
                  </QuantityBtn>
                  <QuantityInput
                    value={quantity}
                    onChange={(e) => {
                      const newQuantity = Math.max(
                        1,
                        Math.min(
                          currentProduct.stockQuantity,
                          parseInt(e.target.value) || 1
                        )
                      );
                      setQuantity(newQuantity);
                    }}
                    min="1"
                    max={currentProduct.stockQuantity}
                  />
                  <QuantityBtn
                    onClick={() => handleQuantityChange("increment")}
                    disabled={quantity >= currentProduct.stockQuantity}
                  >
                    <FiPlus />
                  </QuantityBtn>
                </QuantityControl>
              </OptionsSection>

              <ActionsSection>
                <AddToCartBtn
                  onClick={handleAddToCart}
                  disabled={currentProduct.stockQuantity <= 0}
                >
                  {currentProduct.stockQuantity > 0 ? (
                    <>
                      <FiCheck />
                      Dodaj u korpu
                    </>
                  ) : (
                    "Nema na stanju"
                  )}
                </AddToCartBtn>

                <ShareBtn onClick={shareProduct}>
                  <FiShare2 />
                </ShareBtn>
              </ActionsSection>

              <FeaturesList>
                <Feature>
                  <FiTruck />
                  <span>Besplatna dostava preko {formatPrice(3000)}</span>
                </Feature>
                <Feature>
                  <FiShield />
                  <span>Garancija kvaliteta</span>
                </Feature>
                <Feature>
                  <FiRefreshCw />
                  <span>30 dana za povraćaj</span>
                </Feature>
              </FeaturesList>
            </ProductInfo>
          </ProductLayout>

          <TabSection>
            <TabNavigation>
              <TabButton
                $active={activeTab === "description"}
                onClick={() => setActiveTab("description")}
              >
                Opis
              </TabButton>
              <TabButton
                $active={activeTab === "specifications"}
                onClick={() => setActiveTab("specifications")}
              >
                Specifikacije
              </TabButton>
            </TabNavigation>

            <TabContent>
              {activeTab === "description" && (
                <div>
                  <TabHeading>O proizvodu</TabHeading>
                  <p>{currentProduct.description}</p>
                  <p>
                    Ovaj proizvod je deo oficijalne kolekcije benda{" "}
                    {currentProduct.band?.name}. Dizajn je napravljen u saradnji
                    sa bendom i predstavlja autentičnu memorabiliju za fanove.
                  </p>
                  <TabSubHeading>Karakteristike:</TabSubHeading>
                  <TabList>
                    <TabListItem>✓ Visokokvalitetni materijali</TabListItem>
                    <TabListItem>✓ 100% pamuk (Gildan Premium)</TabListItem>
                    <TabListItem>✓ Originalni dizajn</TabListItem>
                    <TabListItem>✓ Oficijalno licencirani proizvod</TabListItem>
                    <TabListItem>✓ Dugotrajnost boja</TabListItem>
                  </TabList>
                </div>
              )}

              {activeTab === "specifications" && (
                <div>
                  <TabHeading>Tehničke specifikacije</TabHeading>
                  <SpecTable>
                    <tbody>
                      <SpecRow>
                        <SpecCell $bold>Kategorija:</SpecCell>
                        <SpecCell>{currentProduct.category?.name}</SpecCell>
                      </SpecRow>
                      <SpecRow>
                        <SpecCell $bold>Bend:</SpecCell>
                        <SpecCell>{currentProduct.band?.name}</SpecCell>
                      </SpecRow>
                      <SpecRow>
                        <SpecCell $bold>Dostupne veličine:</SpecCell>
                        <SpecCell>
                          {availableSizes.length > 0
                            ? availableSizes.join(", ")
                            : "Jedna veličina"}
                        </SpecCell>
                      </SpecRow>
                      <SpecRow>
                        <SpecCell $bold>Na stanju:</SpecCell>
                        <SpecCell>{currentProduct.stockQuantity} kom</SpecCell>
                      </SpecRow>
                      <SpecRow>
                        <SpecCell $bold>Status:</SpecCell>
                        <SpecCell>
                          {currentProduct.isNew && "Novo • "}
                          {currentProduct.isFeatured && "Istaknuto • "}
                          {currentProduct.isLimited && "Limitirana edicija • "}
                          {currentProduct.isBestSeller && "Bestseller"}
                        </SpecCell>
                      </SpecRow>
                    </tbody>
                  </SpecTable>
                </div>
              )}
            </TabContent>
          </TabSection>

          <SimilarProductsSection>
            <SectionTitle>Slični proizvodi</SectionTitle>
            <ProductShowcase>
              {similarProducts.loading ? (
                <EmptyProductsContainer>
                  Učitavam slične proizvode...
                </EmptyProductsContainer>
              ) : similarProducts.data?.length === 0 ? (
                <EmptyProductsContainer>
                  Trenutno nema sličnih proizvoda za prikaz.
                </EmptyProductsContainer>
              ) : (
                similarProducts.data?.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    hideAddToCart
                  />
                ))
              )}
            </ProductShowcase>
          </SimilarProductsSection>
        </Container>
      </MainContainer>

      {/* Image Modal */}
      {showImageModal && (
        <ImageModal onClick={closeImageModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeImageModal}>×</CloseButton>
            <img src={productImages[selectedImage]} alt={currentProduct.name} />
          </ModalContent>
        </ImageModal>
      )}

      {/* Size Guide Modal */}
      <TShirtSizeGuide
        isOpen={showSizeGuide}
        onClose={() => setShowSizeGuide(false)}
        productDimensions={formatDimensionsForSizeGuide(productDimensions.data)}
      />
    </>
  );
}
